/**
 * Authenticated fetch wrapper that handles token expiration
 * Automatically triggers re-authentication when a 401 response is received
 */

import { logger } from './logger';

let forceReauthCallback: (() => void) | null = null;
const MAX_AUTH_FAILURES = 2;
const REDIRECT_DELAY_MS = 2000;
const AUTH_FAILURE_KEY = 'authFailureCount';
const LAST_REDIRECT_KEY = 'lastAuthRedirect';
const REDIRECT_WINDOW_MS = 30000; // 30 seconds - if we redirect again within this window, count it as a repeat

/**
 * Register a callback to be invoked when re-authentication is needed
 */
export const setForceReauthCallback = (callback: () => void) => {
  forceReauthCallback = callback;
};

/**
 * Get the current auth failure count from sessionStorage
 */
const getAuthFailureCount = (): number => {
  const stored = sessionStorage.getItem(AUTH_FAILURE_KEY);
  return stored ? parseInt(stored, 10) || 0 : 0;
};

/**
 * Increment the auth failure count in sessionStorage
 */
const incrementAuthFailureCount = (): number => {
  const count = getAuthFailureCount() + 1;
  sessionStorage.setItem(AUTH_FAILURE_KEY, count.toString());
  return count;
};

/**
 * Check if we're in a redirect loop (redirecting too frequently)
 */
const isInRedirectLoop = (): boolean => {
  const lastRedirect = sessionStorage.getItem(LAST_REDIRECT_KEY);
  if (!lastRedirect) return false;

  const timeSinceLastRedirect = Date.now() - parseInt(lastRedirect, 10);
  return timeSinceLastRedirect < REDIRECT_WINDOW_MS;
};

/**
 * Record that we're redirecting now
 */
const recordRedirect = () => {
  sessionStorage.setItem(LAST_REDIRECT_KEY, Date.now().toString());
};

/**
 * Reset the auth failure counter (call this on manual logout or successful API call)
 */
export const resetAuthFailureCounter = () => {
  sessionStorage.removeItem(AUTH_FAILURE_KEY);
  sessionStorage.removeItem(LAST_REDIRECT_KEY);
};

/**
 * Wrapped fetch that automatically handles 401 responses by triggering re-authentication
 * Includes circuit breaker to prevent infinite redirect loops
 */
export const authenticatedFetch = async (url: string, options?: RequestInit): Promise<Response> => {
  const response = await fetch(url, options);

  // Log permission denials (403 Forbidden)
  if (response.status === 403) {
    logger.warn('Permission denied - insufficient access', {
      status: 403,
      url,
      method: options?.method || 'GET'
    });
  }

  // If we get a 401, the token is expired or invalid
  if (response.status === 401) {
    const failureCount = incrementAuthFailureCount();
    const inLoop = isInRedirectLoop();

    logger.warn('Authentication failed - token expired or invalid', {
      status: 401,
      url,
      failureCount,
      maxFailures: MAX_AUTH_FAILURES,
      inRedirectLoop: inLoop
    });

    console.warn(`Token expired or invalid (401) - attempt ${failureCount}/${MAX_AUTH_FAILURES}`, {
      url,
      inLoop
    });

    // Circuit breaker: if we're in a redirect loop or have failed too many times, stop redirecting
    if (failureCount > MAX_AUTH_FAILURES || inLoop) {
      logger.error('Auth circuit breaker activated - stopping to prevent infinite redirect loop', {
        failureCount,
        maxFailures: MAX_AUTH_FAILURES,
        inRedirectLoop: inLoop,
        url
      });

      console.error('Auth redirect loop detected - stopping to prevent infinite loop', {
        failureCount,
        inLoop,
        url
      });

      // Clear the counter so user can try again later
      resetAuthFailureCounter();

      throw new Error(
        'You are not authenticated to access migration logs. Your login succeeded, but you do not ' +
        'have the required permissions for this feature. Please contact your administrator for assistance.'
      );
    }

    // Clear stale token data
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('genesysCloudToken');

    // Record this redirect
    recordRedirect();

    // Trigger re-authentication if callback is registered
    if (forceReauthCallback) {
      // Add a delay to give the UI time to show error messages
      setTimeout(() => {
        forceReauthCallback?.();
      }, REDIRECT_DELAY_MS);
    } else {
      // Fallback: redirect to login if no callback registered
      setTimeout(() => {
        window.location.href = '/';
      }, REDIRECT_DELAY_MS);
    }

    throw new Error('Authentication token expired. Please log in again.');
  }

  // 403 means the request is authenticated but not authorized.
  // Let the UI recover in-place and offer a re-login action.
  if (response.status === 403) {
    console.warn('Request was forbidden (403), user may need to re-authenticate');
    throw new Error('forbidden');
  }

  // Reset counter on successful request (but not on login, let it accumulate across logins)
  if (response.ok && !url.includes('/Validate')) {
    resetAuthFailureCounter();
  }

  return response;
};
